import { useState, useEffect, useCallback, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import debug from '@/lib/debug';

/**
 * HOTFIX-1 — how long the app shell will wait for Supabase to tell us
 * whether there IS a session before it gives up and says so.
 *
 * `loading` starts true and `ProtectedRoute` renders a full-screen
 * spinner while it is. Before this, the ONLY thing that could clear it
 * was `getSession()` resolving or an auth event arriving — there was no
 * `.catch()` and no timeout, so a rejected or never-settling
 * `getSession()` (Supabase unreachable, DNS stalled, a hung service
 * worker, a paused project) left the studio on "One sec — bringing your
 * studio up." forever, with no error and no way out but a manual
 * refresh. That is a hang with no terminal state, and it is exactly the
 * shape of the "just loads endlessly" report.
 *
 * 10s is deliberately generous — a slow phone on hotel wifi should
 * still make it — but it is finite, which is the whole point.
 */
const AUTH_SESSION_TIMEOUT_MS = 10_000;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  /**
   * HOTFIX-1 — non-null when we could not determine the session state:
   * `getSession()` rejected, returned an error, or never answered inside
   * AUTH_SESSION_TIMEOUT_MS. `loading` is always false when this is set,
   * so consumers have a terminal state to render instead of a spinner.
   *
   * NOTE: "we don't know" is not "signed out". Consumers must show an
   * error with a retry, not bounce the user to /auth — a redirect on a
   * network blip would look like a random forced sign-out.
   */
  sessionError: Error | null;
  /** HOTFIX-1 — re-run the session bootstrap. Clears `sessionError`. */
  retrySession: () => void;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  // LAUNCH-2 — `termsAcceptedAt` is an ISO timestamp captured when the
  // visitor ticked the Terms box on the signup form. Optional so the
  // other callers of signUp are unchanged, but the web form always
  // passes it: an acceptance nobody recorded is an acceptance we
  // cannot show anyone later.
  signUp: (email: string, password: string, fullName: string, orgName: string, termsAcceptedAt?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper function to wait for profile to be created by the trigger
const waitForProfile = async (userId: string, maxAttempts = 15, delayMs = 400): Promise<boolean> => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('id, org_id')
      .eq('id', userId)
      .maybeSingle();
    
    if (error) {
      debug.warn(`Profile check attempt ${attempt + 1} failed`, error);
    }
    
    if (profile) {
      debug.log(`Profile found after ${attempt + 1} attempts`, { hasOrgId: !!profile.org_id });
      return true;
    }
    
    await new Promise(resolve => setTimeout(resolve, delayMs));
  }
  debug.warn('Profile not found after max attempts');
  return false;
};

// Generate a URL-safe slug from org name
const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 50) || 'org';
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  // HOTFIX-1 — terminal failure state for the session bootstrap.
  const [sessionError, setSessionError] = useState<Error | null>(null);
  // Bumping this re-runs the bootstrap effect below. That's the whole
  // implementation of "Try again" on the error screen.
  const [retryNonce, setRetryNonce] = useState(0);

  const retrySession = useCallback(() => setRetryNonce((n) => n + 1), []);
  const { toast } = useToast();

  useEffect(() => {
    // HOTFIX-1 — exactly three things can end the initial loading
    // state, and all three are wired below:
    //   1. an auth event arrives (the normal path),
    //   2. getSession() settles — resolved OR rejected,
    //   3. the timeout fires.
    // `settled` makes the transition one-shot so a late resolution
    // can't stomp on an error the user is already looking at, and
    // `cancelled` keeps a slow response from setting state after
    // unmount.
    let cancelled = false;
    let settled = false;
    let timeoutId: number | null = null;

    const clearTimer = () => {
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    /** Apply a session we actually received. Always safe to call. */
    const applySession = (next: Session | null) => {
      if (cancelled) return;
      setSession(next);
      setUser(next?.user ?? null);
    };

    /** End the loading state, once, with or without an error. */
    const settle = (error: Error | null) => {
      if (cancelled || settled) return;
      settled = true;
      clearTimer();
      setSessionError(error);
      setLoading(false);
    };

    setLoading(true);
    setSessionError(null);

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        debug.log('Auth state change', { event });
        applySession(session);
        // A live auth event proves the client is reachable, so it also
        // clears any timeout error already on screen — including one
        // raised by an earlier attempt in this same mount.
        settled = true;
        clearTimer();
        if (!cancelled) {
          setSessionError(null);
          setLoading(false);
        }

        // AUTH-RESET-1 — the recovery link's redirect can land on `/`
        // instead of /auth/reset (Supabase falls back to the Site URL
        // when the redirectTo isn't allow-listed), which leaves the
        // user "just logged in" holding a single-use recovery session
        // with no form to spend it on. Whatever URL the hash lands
        // on, this event fires exactly once — route to the reset form
        // while the session is still fresh. Hard navigation on
        // purpose: AuthProvider mounts outside the Router, and the
        // reset page does its own session check on mount.
        if (event === 'PASSWORD_RECOVERY' && !window.location.pathname.startsWith('/auth/reset')) {
          window.location.assign('/auth/reset');
        }

        // Clear the developer-mode flag on a fresh sign-in so a
        // new studio signing up in a browser that used to have dev
        // tooling enabled starts clean.
        //
        // Wave 12 — the sister write to `orbit_demo_mode` was
        // removed. That legacy UserDefaults-style key is a no-op
        // now (Preview Studio is session-scoped in memory via
        // DemoModeContext, which also wipes the stale key on
        // mount). Writing "false" here was dead defensive code.
        //
        // SIGNED_IN fires once per actual login (not on every re-render
        // or session refresh), so clearing here is safe and minimal.
        if (event === 'SIGNED_IN') {
          try {
            window.localStorage.setItem('orbit_developer_mode', 'false');
          } catch {
            /* private mode — ignore */
          }
        }
      }
    );

    // THEN check for existing session.
    //
    // HOTFIX-1 — this used to be a bare `.then()`. A `.catch()` is not
    // optional on a promise the whole app shell is gated behind: the
    // rejected path left `loading` stuck true AND produced an unhandled
    // rejection, so the failure was invisible in both the UI and the
    // console's error list.
    supabase.auth
      .getSession()
      .then(({ data: { session }, error }) => {
        applySession(session ?? null);
        if (error) {
          debug.error({ action: 'auth_session_bootstrap' }, error);
          settle(
            new Error(
              error.message ||
                "We couldn't confirm your session. Check your connection and try again.",
            ),
          );
          return;
        }
        settle(null);
      })
      .catch((err: unknown) => {
        const error = err instanceof Error ? err : new Error(String(err));
        debug.error({ action: 'auth_session_bootstrap' }, error);
        settle(error);
      });

    // HOTFIX-1 — the guaranteed escape hatch. If neither an auth event
    // nor getSession() has answered by now, stop rendering a spinner
    // and hand the user a real error with a retry.
    timeoutId = window.setTimeout(() => {
      const error = new Error(
        `Supabase didn't answer in ${Math.round(
          AUTH_SESSION_TIMEOUT_MS / 1000,
        )}s, so we can't tell whether you're signed in.`,
      );
      debug.error({ action: 'auth_session_timeout' }, error);
      settle(error);
    }, AUTH_SESSION_TIMEOUT_MS);

    return () => {
      cancelled = true;
      clearTimer();
      subscription.unsubscribe();
    };
    // `retryNonce` is the retry button: bumping it tears this down and
    // runs the whole bootstrap again from scratch.
  }, [retryNonce]);

  const signIn = async (email: string, password: string) => {
    try {
      debug.log('Signing in', { email });
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        debug.error({ action: 'sign_in', payload: { email } }, error);
        toast({
          title: "Sign in failed",
          description: debug.parseSupabaseError(error),
          variant: "destructive",
        });
        return { error };
      }
      
      debug.success('Signed in', { email });
      toast({
        title: "Welcome back!",
        description: "You have successfully signed in.",
      });
      
      return { error: null };
    } catch (err) {
      const error = err as Error;
      debug.error({ action: 'sign_in_unexpected' }, error);
      return { error };
    }
  };

  const signUp = async (email: string, password: string, fullName: string, orgName: string, termsAcceptedAt?: string) => {
    try {
      debug.log('Starting signup', { email, orgName });
      const redirectUrl = `${window.location.origin}/`;
      
      // Step 1: Create the auth user - store org name in metadata for onboarding to use later
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName,
            org_name: orgName, // Stored for onboarding wizard to use
            // LAUNCH-2 — stored on the auth user so acceptance is
            // provable without a migration. `terms_version` is the
            // date the Terms page was last revised; without it a
            // timestamp only proves they agreed to *something*.
            ...(termsAcceptedAt
              ? { terms_accepted_at: termsAcceptedAt, terms_version: '2026-09-14' }
              : {}),
          },
        },
      });

      if (authError) {
        debug.error({ action: 'signup-auth', payload: { email } }, authError);
        toast({
          title: "Sign up failed",
          description: debug.parseSupabaseError(authError),
          variant: "destructive",
        });
        return { error: authError };
      }

      if (!authData.user) {
        const error = new Error('No user returned from signup');
        debug.error({ action: 'signup-auth' }, error);
        toast({
          title: "Sign up failed",
          description: "Unable to create account. Please try again.",
          variant: "destructive",
        });
        return { error };
      }

      const userId = authData.user.id;
      debug.log('User created', { userId });

      // Step 2: Wait for the profile trigger to create the profile
      const profileExists = await waitForProfile(userId);
      
      if (!profileExists) {
        debug.warn('Profile not created by trigger, attempting manual creation');
        const { error: manualProfileError } = await supabase
          .from('profiles')
          .insert({
            id: userId,
            email: email,
            full_name: fullName,
          });
        
        if (manualProfileError) {
          debug.error({ action: 'manual-profile-create', userId, table: 'profiles' }, manualProfileError);
        }
      } else {
        // Update profile with full name
        await supabase
          .from('profiles')
          .update({ full_name: fullName })
          .eq('id', userId);
      }

      // NOTE: Organization creation is now deferred to the OnboardingWizard
      // This ensures users go through theme selection and plan selection first

      debug.success('Signup complete - user will proceed to onboarding', { userId });
      toast({
        title: "Account created!",
        description: "Welcome to Orbit. Let's set up your workspace.",
      });

      return { error: null };
    } catch (err) {
      const error = err as Error;
      debug.error({ action: 'signup-unexpected' }, error);
      toast({
        title: "Sign up failed",
        description: error.message,
        variant: "destructive",
      });
      return { error };
    }
  };

  const signOut = async () => {
    debug.log('Signing out');
    await supabase.auth.signOut();
    debug.success('Signed out');
    toast({
      title: "Signed out",
      description: "You have been signed out successfully.",
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        // HOTFIX-1 — additive; every existing consumer destructures
        // only the fields it needs, so nothing else has to change.
        sessionError,
        retrySession,
        signIn,
        signUp,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
